import { useEffect } from 'react';

import type { AlineSelection } from '@aline/common';
import { debounce } from 'debounce';
import type { Editor } from 'slate';
import { Range as SlateRange } from 'slate';
import { useCurrentUserId } from 'src/gql';
import type { AlineClientEditor } from 'src/layout-engine';
import { useEditor, useSelection } from 'src/layout-engine';

import type { SuggestionUnit } from './suggestion-units';
import { getSuggestionUnitRange } from './suggestion-units';

/**
 * Navigation is the only thing that selects an AI edit. Selection sync should
 * only keep that selected edit while the editor selection still matches the
 * unit range chosen by navigation.
 */
export function getDoesSelectionMatchSelectedSuggestionUnit(
  editor: Editor,
  options: {
    currentUserId: string | null;
    selectedSuggestionGroupId: string | null;
    selection: AlineSelection | null;
    suggestionUnits: SuggestionUnit[];
  }
): boolean {
  const { currentUserId, selectedSuggestionGroupId, selection, suggestionUnits } = options;
  if (!selection || !currentUserId || !selectedSuggestionGroupId) return false;

  const selectedUnit = suggestionUnits.find(unit => unit.groupId === selectedSuggestionGroupId);
  if (!selectedUnit) return false;

  const unitRange = getSuggestionUnitRange(editor, selectedUnit);
  if (!unitRange) return false;

  return SlateRange.equals(selection, unitRange);
}

/**
 * Debounced function to clear the selected suggestion id.
 *
 * Debounced to 250ms so toolbar navigation can finish applying its programmatic
 * selection before selection sync decides whether the user moved away.
 */
const clearSelectedSuggestionGroupId = debounce(
  (
    editor: AlineClientEditor,
    options: {
      currentUserId: string | null;
      selectedSuggestionGroupId: string | null;
      selection: AlineSelection | null;
      setSelectedSuggestionGroupId: (suggestionId: string | null) => void;
      suggestionUnits: SuggestionUnit[];
    }
  ) => {
    if (!options.selectedSuggestionGroupId) return;
    const selectionMatchesSelectedUnit = getDoesSelectionMatchSelectedSuggestionUnit(
      editor,
      options
    );
    if (selectionMatchesSelectedUnit) return;
    options.setSelectedSuggestionGroupId(null);
  },
  250
);

/**
 * Clears the selected suggestion when the user moves the editor selection
 * away from the unit selected by the toolbar navigation controls.
 */
export function useClearSelectedSuggestionMarkerOnSelectionChange(options: {
  selectedSuggestionGroupId: string | null;
  setSelectedSuggestionGroupId: (suggestionId: string | null) => void;
  suggestionUnits: SuggestionUnit[];
}): void {
  const editor = useEditor();
  const selection = useSelection();
  const currentUserId = useCurrentUserId();
  const { selectedSuggestionGroupId, setSelectedSuggestionGroupId, suggestionUnits } = options;

  useEffect(() => {
    clearSelectedSuggestionGroupId(editor, {
      currentUserId,
      selectedSuggestionGroupId,
      selection,
      setSelectedSuggestionGroupId,
      suggestionUnits,
    });
  }, [
    editor,
    currentUserId,
    selectedSuggestionGroupId,
    selection,
    setSelectedSuggestionGroupId,
    suggestionUnits,
  ]);
}
